import Box from "@mui/material/Box";
import { getSelectableUomNames, isActiveUom } from "../../../../../utils/uomOptions";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import { useDetailedStRequest } from "../../../context/DetailedStRequestContext";
import { useFormik } from "formik";
import {
  REQUEST_PROCESS_RESULT_INITIAL_VALUES,
  stRequestProcessResultSchema,
} from "../../../shared";
import type { STRequestProcessResult } from "../../../types";
import { deriveResultValue } from "./helpers";

type EditStRequestProcessResultProps = {
  open: boolean;
  close(): void;
  initialValues: STRequestProcessResult | null;
  onSave(
    stRequestProcessResult: STRequestProcessResult,
    previousStRequestProcessResult: STRequestProcessResult | null,
  ): void;
};

export function EditStRequestProcessResult({
  open,
  close,
  initialValues,
  onSave,
}: EditStRequestProcessResultProps) {
  const { uoms, processComponentOptions } = useDetailedStRequest();

  const requestProcessResultFormik = useFormik({
    initialValues: initialValues ?? REQUEST_PROCESS_RESULT_INITIAL_VALUES,
    onSubmit: (value, { setValues, setFieldTouched }) => {
      const resultValue = deriveResultValue(value);

      const stRequestProcessResultToSubmit: STRequestProcessResult = {
        ...value,
        resultValue,
      };

      onSave(stRequestProcessResultToSubmit, initialValues);
      setValues(REQUEST_PROCESS_RESULT_INITIAL_VALUES);
      setFieldTouched("resultComponentName", false);
      setFieldTouched("resultText", false);
      close();
    },
    validationSchema: stRequestProcessResultSchema,
    validateOnMount: false,
    enableReinitialize: true,
  });

  return (
    <Dialog fullWidth={true} maxWidth="md" open={open} onClose={close}>
      <DialogTitle>ST Request Process Result</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          display="grid"
          gridTemplateColumns="1fr 1fr 1fr"
          gap={1}
          style={{ paddingTop: "1rem" }}>
          <Autocomplete
            id="resultComponentName"
            value={requestProcessResultFormik.values.processComponentName}
            options={processComponentOptions}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Result Component Name"
                size="small"
                helperText={requestProcessResultFormik.errors.processComponentName || " "}
                error={
                  requestProcessResultFormik.touched.processComponentName &&
                  Boolean(requestProcessResultFormik.errors.processComponentName)
                }
                required
              />
            )}
            autoSelect
            onChange={(_, v) => {
              requestProcessResultFormik.setFieldValue("processComponentName", v);
            }}
            onBlur={requestProcessResultFormik.handleBlur}
            disabled={!!initialValues}
          />
          <TextField
            name="resultText"
            id="resultText"
            label="Value"
            size="small"
            onChange={requestProcessResultFormik.handleChange}
            onBlur={requestProcessResultFormik.handleBlur}
            value={requestProcessResultFormik.values.resultText}
            helperText={requestProcessResultFormik.errors.resultText || ""}
            error={
              requestProcessResultFormik.touched.resultText &&
              Boolean(requestProcessResultFormik.errors.resultText)
            }
            required
          />
          <Autocomplete
            id="uom"
            value={requestProcessResultFormik.values.resultUoMName}
            options={getSelectableUomNames(uoms, requestProcessResultFormik.values.resultUoMName)}
            getOptionDisabled={(name) => !isActiveUom(uoms, name)}
            renderInput={(params) => <TextField {...params} label="UoM" size="small" />}
            autoSelect
            onChange={(_, v) => {
              requestProcessResultFormik.setFieldValue("resultUoMName", v);
            }}
            onBlur={requestProcessResultFormik.handleBlur}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Close</Button>
        <Button onClick={requestProcessResultFormik.submitForm}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
